/**
 * B709 leg 2 — the member portal's reads, under the B632 session:
 * `membership.list` (fenced to the email's own members) and each plan ONCE
 * (`membership.plan.get`). A plan that will not read leaves its card standing
 * on the member record alone; a 401/403 is the session ending on the server's
 * clock, handed back to the controller to sign in again.
 */
import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../lib/api.js";
import { portalMembers, portalPlan, type PortalMember, type PortalPlan } from "../lib/meMembership.js";

interface Caller {
  call(op: string, fields: Record<string, unknown>): Promise<unknown>;
}

export interface MeMembershipReads {
  readonly members: readonly PortalMember[] | undefined;
  readonly plans: Readonly<Record<string, PortalPlan | undefined>>;
  readonly error: string | undefined;
  readonly retry: () => void;
}

export function useMeMembership(signedIn: Caller | undefined, onSignedOut: () => void): MeMembershipReads {
  const [members, setMembers] = useState<readonly PortalMember[] | undefined>(undefined);
  const [plans, setPlans] = useState<Readonly<Record<string, PortalPlan | undefined>>>({});
  const [error, setError] = useState<string | undefined>(undefined);
  const [round, setRound] = useState(0);

  useEffect(() => {
    if (signedIn === undefined) return;
    let gone = false;
    setMembers(undefined);
    setError(undefined);
    void (async () => {
      const list = portalMembers(await signedIn.call("membership.list", {}));
      const read: Record<string, PortalPlan | undefined> = {};
      for (const planId of [...new Set(list.map((m) => m.planId))]) {
        read[planId] = await signedIn
          .call("membership.plan.get", { planId })
          .then(portalPlan)
          .catch(() => undefined);
      }
      if (gone) return;
      setPlans(read);
      setMembers(list);
    })().catch((e: unknown) => {
      if (gone) return;
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        onSignedOut();
        return;
      }
      setError("We couldn't read your membership just now. Nothing changed — your cards still work at the gate.");
    });
    return () => {
      gone = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the sign-out seam is fixed for the page's life
  }, [signedIn, round]);

  const retry = useCallback(() => setRound((r) => r + 1), []);
  return { members, plans, error, retry };
}
